/**
 * ModelFrameStreamer - Handles model-only still capture and LiveKit quality hints
 *
 * This component detects when the current user is selected as the model,
 * biases their LiveKit camera track toward detail-first encoding during poses,
 * relaxes the track during breaks, and queues local still captures for upload
 * once capture pauses or stops.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useLocalParticipant, useTracks } from '@livekit/components-react';
import { Track, VideoQuality } from 'livekit-client';
import type { LocalTrackPublication } from 'livekit-client';
import { useRoom } from '../room/RoomProvider';
import { useModelFrameStream } from './useModelFrameStream';
import { getQueuedReferencePackAssetCount, uploadQueuedReferencePackAssets } from './ReferencePackAssetUploadQueue';
import { DEFAULT_HI_RES_TUNING_SETTINGS } from './HiResTuningPanel';
import type { HiResTuningSettings } from './HiResTuningPanel';
import { logVideoDiagnostic } from './videoDiagnostics';
import { getCameraDeviceKey, getStoredCameraDeviceLabel } from './cameraDeviceLabels';
import { getLiveKitVideoResolution, isActiveModelCameraParticipant } from './liveKitCameraProfiles';
import { getModelLowResolutionRecoveryAction } from './localCameraPublishProfileLogic';
import {
  summarizeMediaStreamTrackState,
  summarizeSenderParameters,
} from './videoTrackDiagnostics';

const MIN_PHOTO_INTERVAL_MS = 10_000;
const MAX_PHOTOS_PER_POSE = 20;
const LONG_POSE_THRESHOLD_SECONDS = 200;
const MODEL_CAMERA_QUALITY_CHECK_INTERVAL_MS = 4000;
const MODEL_CAMERA_LOW_RES_STREAK_LIMIT = 2;
const MODEL_CAMERA_RESTART_STREAK_LIMIT = 4;
const MODEL_CAMERA_RECOVERY_COOLDOWN_MS = 20_000;
const MODEL_CAMERA_FALLBACK_TARGET_WIDTH = 2560;
const MODEL_CAMERA_FALLBACK_TARGET_HEIGHT = 1440;
const MODEL_CAMERA_MIN_USEFUL_SHORT_EDGE = 700;
const MODEL_CAMERA_MIN_USEFUL_PIXELS = 1280 * 720;
const MODEL_CAMERA_SWITCH_SETTLE_MS = 1000;

type StillCaptureConstraints = MediaTrackConstraints & {
  resizeMode?: string;
};

type VideoResolutionSample = {
  width: number;
  height: number;
  framesPerSecond?: number | null;
  qualityLimitationReason?: string | null;
};

type CameraCaptureInfo = {
  cameraId: string;
  cameraLabel: string;
};

function shouldUseLiveKitTrackForStillCapture() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (nav.userAgentData?.mobile === true) return true;
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return true;

  return window.matchMedia?.('(pointer: coarse)').matches ?? false;
}

type DetailFirstVideoTrack = {
  mediaStreamTrack?: MediaStreamTrack;
  sender?: RTCRtpSender;
  getRTCStatsReport?: () => Promise<RTCStatsReport | undefined>;
  setDegradationPreference?: (preference: RTCDegradationPreference) => void | Promise<void>;
  setPublishingQuality?: (maxQuality: VideoQuality) => void;
};

interface ModelFrameStreamerProps {
  tuning?: HiResTuningSettings;
}

function getResolutionSampleFromSettings(settings: MediaTrackSettings): VideoResolutionSample | null {
  if (!settings.width || !settings.height) return null;

  return {
    width: settings.width,
    height: settings.height,
    framesPerSecond: settings.frameRate ?? null,
  };
}

function isUnusableModelResolution(sample: VideoResolutionSample | null) {
  if (!sample) return false;

  const shortEdge = Math.min(sample.width, sample.height);
  const pixels = sample.width * sample.height;

  return shortEdge < MODEL_CAMERA_MIN_USEFUL_SHORT_EDGE ||
    pixels < MODEL_CAMERA_MIN_USEFUL_PIXELS;
}

function getCapabilityMax(range: { max?: number } | undefined, fallback: number) {
  return typeof range?.max === 'number' && Number.isFinite(range.max)
    ? range.max
    : fallback;
}

function getPreferredModelCameraTarget(
  mediaStreamTrack: MediaStreamTrack,
  tuning: HiResTuningSettings
) {
  const settings = mediaStreamTrack.getSettings();
  const requestedResolution = getLiveKitVideoResolution(tuning);
  const requestedWidth = requestedResolution.width ?? MODEL_CAMERA_FALLBACK_TARGET_WIDTH;
  const requestedHeight = requestedResolution.height ?? MODEL_CAMERA_FALLBACK_TARGET_HEIGHT;
  const requestedFrameRate = requestedResolution.frameRate ?? tuning.liveKitMaxFramerate;
  const longEdge = Math.max(requestedWidth, requestedHeight);
  const shortEdge = Math.min(requestedWidth, requestedHeight);
  const isPortrait = (settings.height ?? 0) > (settings.width ?? 0);
  const targetWidth = isPortrait ? shortEdge : longEdge;
  const targetHeight = isPortrait ? longEdge : shortEdge;
  const capabilities = mediaStreamTrack.getCapabilities?.();
  const widthMax = getCapabilityMax(capabilities?.width, targetWidth);
  const heightMax = getCapabilityMax(capabilities?.height, targetHeight);
  const frameRateMax = getCapabilityMax(capabilities?.frameRate, requestedFrameRate);
  const width = Math.max(640, Math.min(targetWidth, widthMax));
  const height = Math.max(480, Math.min(targetHeight, heightMax));
  const frameRate = Math.max(1, Math.min(requestedFrameRate, frameRateMax));
  const maxFrameRate = Math.max(frameRate, Math.min(tuning.liveKitMaxFramerate, frameRateMax));

  return {
    width,
    height,
    minWidth: Math.min(1280, width),
    minHeight: Math.min(720, height),
    frameRate,
    maxFrameRate,
  };
}

async function applyDetailFirstSenderPreferences(
  track: DetailFirstVideoTrack,
  tuning: HiResTuningSettings
) {
  await track.setDegradationPreference?.('maintain-resolution');
  track.setPublishingQuality?.(VideoQuality.HIGH);

  const sender = track.sender;
  if (!sender) return false;

  const parameters = sender.getParameters() as RTCRtpSendParameters & {
    degradationPreference?: RTCDegradationPreference;
  };
  parameters.degradationPreference = 'maintain-resolution';

  let changedEncoding = false;
  if (parameters.encodings?.length) {
    const modelMaxBitrate = tuning.liveKitMaxBitrateMbps * 1_000_000;
    const modelMaxFramerate = tuning.liveKitMaxFramerate;

    parameters.encodings.forEach((encoding, index) => {
      const editableEncoding = encoding as RTCRtpEncodingParameters & {
        priority?: RTCPriorityType;
        networkPriority?: RTCPriorityType;
      };
      const isHighestLayer = index === (parameters.encodings?.length ?? 1) - 1;

      if (isHighestLayer && (!editableEncoding.maxBitrate || editableEncoding.maxBitrate < modelMaxBitrate)) {
        editableEncoding.maxBitrate = modelMaxBitrate;
        changedEncoding = true;
      }

      if (
        isHighestLayer &&
        editableEncoding.scaleResolutionDownBy &&
        editableEncoding.scaleResolutionDownBy !== 1
      ) {
        editableEncoding.scaleResolutionDownBy = 1;
        changedEncoding = true;
      }

      if (!editableEncoding.maxFramerate || editableEncoding.maxFramerate > modelMaxFramerate) {
        editableEncoding.maxFramerate = modelMaxFramerate;
        changedEncoding = true;
      }

      if (editableEncoding.priority !== 'high') {
        editableEncoding.priority = 'high';
        changedEncoding = true;
      }

      if (editableEncoding.networkPriority !== 'high') {
        editableEncoding.networkPriority = 'high';
        changedEncoding = true;
      }
    });
  }

  if (!changedEncoding && parameters.degradationPreference === 'maintain-resolution') {
    return true;
  }

  await sender.setParameters(parameters);
  return true;
}

async function getLargestOutboundVideoResolution(track: DetailFirstVideoTrack): Promise<VideoResolutionSample | null> {
  const report = await track.getRTCStatsReport?.();
  if (!report) return null;

  let largest: VideoResolutionSample | null = null;

  report.forEach((stat) => {
    const item = stat as Record<string, unknown>;
    if (item.type !== 'outbound-rtp') return;

    const kind = item.kind ?? item.mediaType;
    if (kind && kind !== 'video') return;

    const width = item.frameWidth;
    const height = item.frameHeight;
    if (typeof width !== 'number' || typeof height !== 'number') return;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;
    if (typeof item.framesEncoded === 'number' && item.framesEncoded <= 0) return;

    const sample: VideoResolutionSample = {
      width,
      height,
      framesPerSecond: typeof item.framesPerSecond === 'number' ? item.framesPerSecond : null,
      qualityLimitationReason: typeof item.qualityLimitationReason === 'string'
        ? item.qualityLimitationReason
        : null,
    };

    if (!largest || width * height > largest.width * largest.height) {
      largest = sample;
    }
  });

  return largest;
}

export function ModelFrameStreamer({ tuning = DEFAULT_HI_RES_TUNING_SETTINGS }: ModelFrameStreamerProps) {
  const {
    roomCode,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    participants: sessionParticipants,
    poseTimer,
  } = useRoom();
  const { localParticipant } = useLocalParticipant();
  const useLiveKitStillCaptureOnly = useMemo(() => shouldUseLiveKitTrackForStillCapture(), []);

  // Use useTracks to reactively get the local camera track
  const localTracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const localCameraTrack = localTracks.find(t => t.participant.identity === localParticipant?.identity);

  const videoRef = useRef<HTMLVideoElement>(null);
  const fallbackVideoRef = useRef<HTMLVideoElement>(null);
  const stillCaptureStreamRef = useRef<MediaStream | null>(null);
  const trackPublicationRef = useRef<LocalTrackPublication | null>(null);
  const lowResolutionStreakRef = useRef(0);
  const lastRecoveryAtRef = useRef(0);
  const recoveryInProgressRef = useRef(false);
  const lastLowResolutionRecoveryDeviceIdRef = useRef<string | null>(null);
  const [trackAttached, setTrackAttached] = useState(false);
  const [stillCaptureReady, setStillCaptureReady] = useState(false);
  const [stillCaptureUnavailable, setStillCaptureUnavailable] = useState(false);
  const [stillCaptureCameraInfo, setStillCaptureCameraInfo] = useState<CameraCaptureInfo | null>(null);
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const isUploadingRef = useRef(false);
  const [queuedCaptureCount, setQueuedCaptureCount] = useState(0);

  useEffect(() => {
    let settleTimer: number | null = null;

    const handleSwitchStarted = () => {
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
      }
      lowResolutionStreakRef.current = 0;
      recoveryInProgressRef.current = false;
      lastLowResolutionRecoveryDeviceIdRef.current = null;
      setCameraSwitching(true);
    };

    const handleSwitchFinished = () => {
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
      }

      settleTimer = window.setTimeout(() => {
        setCameraSwitching(false);
        settleTimer = null;
      }, MODEL_CAMERA_SWITCH_SETTLE_MS);
    };

    window.addEventListener('drawclub:camera-device-switch-started', handleSwitchStarted);
    window.addEventListener('drawclub:camera-device-switch-finished', handleSwitchFinished);

    return () => {
      window.removeEventListener('drawclub:camera-device-switch-started', handleSwitchStarted);
      window.removeEventListener('drawclub:camera-device-switch-finished', handleSwitchFinished);
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
      }
    };
  }, []);

  const participantProfileId = useMemo(() => (
    localParticipant?.identity
      ? sessionParticipants.find(participant => participant.participantId === localParticipant.identity)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${roomCode}`)
      : null
  ), [localParticipant?.identity, roomCode, sessionParticipants]);

  // Compute active selected model camera status directly from central state - no local copy.
  const isModel = useMemo(() => isActiveModelCameraParticipant({
    participantId: localParticipant?.identity,
    participantProfileId,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
  }), [
    localParticipant?.identity,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantProfileId,
  ]);

  // Compute poseActive directly from central state - no local copy
  const poseActive = useMemo(() => {
    if (!poseTimer) {
      console.log('[Sender] poseTimer is null, poseActive=false');
      return false;
    }
    const result = poseTimer.isActive && !poseTimer.isPaused && !poseTimer.isBreak && !poseTimer.isUntimedBreak;
    console.log('[Sender] poseActive:', result, { isActive: poseTimer.isActive, isPaused: poseTimer.isPaused });
    return result;
  }, [poseTimer]);

  const breakMode = useMemo(() => {
    return !!poseTimer && poseTimer.isActive && (poseTimer.isBreak || poseTimer.isUntimedBreak);
  }, [poseTimer]);

  const liveKitCameraInfo = useMemo<CameraCaptureInfo | null>(() => {
    const mediaStreamTrack = localCameraTrack?.publication?.track?.mediaStreamTrack;
    if (!mediaStreamTrack) return null;

    const fallbackCameraId = localCameraTrack?.publication?.trackSid ?? mediaStreamTrack.id;
    const cameraId = getCameraDeviceKey(mediaStreamTrack.getSettings(), fallbackCameraId);
    return {
      cameraId,
      cameraLabel: getStoredCameraDeviceLabel(roomCode, cameraId, 0),
    };
  }, [localCameraTrack?.publication?.track, localCameraTrack?.publication?.trackSid, roomCode]);

  const captureCameraInfo = stillCaptureCameraInfo ?? liveKitCameraInfo;
  const captureModelProfileId = modelProfileId ?? participantProfileId;

  const captureContext = useMemo(() => ({
    modelProfileId: captureModelProfileId,
    poseName: poseTimer?.poseName ?? null,
    poseIndex: poseTimer?.currentPose ?? null,
    cameraId: captureCameraInfo?.cameraId ?? null,
    cameraLabel: captureCameraInfo?.cameraLabel ?? null,
  }), [
    captureCameraInfo?.cameraId,
    captureCameraInfo?.cameraLabel,
    captureModelProfileId,
    poseTimer?.poseName,
    poseTimer?.currentPose,
  ]);

  const captureIntervalMs = useMemo(() => {
    if (tuning.sequentialFrameIntervalMs > 0) {
      return tuning.sequentialFrameIntervalMs;
    }

    const durationSeconds = poseTimer?.duration ?? 0;
    if (durationSeconds > LONG_POSE_THRESHOLD_SECONDS) {
      const durationMs = durationSeconds * 1000;
      return Math.max(
        MIN_PHOTO_INTERVAL_MS,
        Math.floor(durationMs / MAX_PHOTOS_PER_POSE) + 1
      );
    }

    return MIN_PHOTO_INTERVAL_MS;
  }, [poseTimer?.duration, tuning.sequentialFrameIntervalMs]);

  const captureKey = useMemo(() => {
    return [
      roomCode,
      poseTimer?.startedAt ?? 'not-started',
      poseTimer?.currentPose ?? 'no-pose',
      poseTimer?.poseName ?? 'untitled-pose',
    ].join(':');
  }, [poseTimer?.currentPose, poseTimer?.poseName, poseTimer?.startedAt, roomCode]);

  const highResCaptureConfig = useMemo(() => ({
    resolution: tuning.sequentialResolution,
    format: tuning.sequentialFormat,
    quality: tuning.sequentialQuality,
    chunkSize: tuning.sequentialChunkSizeKb * 1024,
  }), [
    tuning.sequentialResolution,
    tuning.sequentialFormat,
    tuning.sequentialQuality,
    tuning.sequentialChunkSizeKb,
  ]);

  const shouldCaptureProductFrames = tuning.queueProductCaptures;
  const canCaptureHighRes =
    poseActive &&
    isModel &&
    !cameraSwitching &&
    shouldCaptureProductFrames &&
    (stillCaptureReady || (stillCaptureUnavailable && trackAttached));

  const uploadQueuedCaptures = useCallback(async ({
    uploadPhase,
    limit,
    signal,
  }: {
    uploadPhase: 'break' | 'session_end';
    limit: number;
    signal: AbortSignal;
  }) => {
    if (isUploadingRef.current) {
      console.log('[ModelFrameStreamer] Upload skipped because an upload is already in progress', {
        roomCode,
        uploadPhase,
      });
      return;
    }

    isUploadingRef.current = true;
    console.log('[ModelFrameStreamer] Uploading queued captures', {
      roomCode,
      uploadPhase,
      limit,
      isModel,
      poseActive,
      breakMode,
    });
    logVideoDiagnostic('info', 'reference-pack-upload-started', {
      uploadPhase,
      limit,
      isModel,
      poseActive,
      breakMode,
    }, {
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
      participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    });

    try {
      await uploadQueuedReferencePackAssets({
        roomCode,
        uploadPhase,
        limit,
        signal,
      });
      const count = await getQueuedReferencePackAssetCount(roomCode);
      console.log('[ModelFrameStreamer] Capture queue upload finished', {
        roomCode,
        uploadPhase,
        remainingQueuedCaptures: count,
      });
      logVideoDiagnostic('info', 'reference-pack-upload-finished', {
        uploadPhase,
        remainingQueuedCaptures: count,
      }, {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
        participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
      });
      setQueuedCaptureCount(count);
    } catch (error) {
      if (!signal.aborted) {
        console.error('[ModelFrameStreamer] Capture queue upload failed', {
          roomCode,
          uploadPhase,
          error,
        });
        logVideoDiagnostic('error', 'reference-pack-upload-failed', {
          uploadPhase,
          error,
        }, {
          roomCode,
          participantId: localParticipant?.identity ?? null,
          participantName: localParticipant?.name ?? null,
          participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
        });
      }
    } finally {
      isUploadingRef.current = false;
    }
  }, [breakMode, isModel, localParticipant?.identity, localParticipant?.name, poseActive, roomCode]);

  useEffect(() => {
    if (!isModel) return;

    const track = localCameraTrack?.publication?.track as DetailFirstVideoTrack | undefined;
    if (!track?.mediaStreamTrack) return;

    const applyCameraMode = async () => {
      const mediaStreamTrack = track.mediaStreamTrack;
      if (!mediaStreamTrack || mediaStreamTrack.readyState !== 'live') return;

      const actions: string[] = [];
      const beforeTrack = summarizeMediaStreamTrackState(mediaStreamTrack);
      const beforeSender = summarizeSenderParameters(track.sender);

      if ('contentHint' in mediaStreamTrack) {
        mediaStreamTrack.contentHint = 'detail';
        actions.push('content-hint');
      }

      try {
        const senderUpdated = await applyDetailFirstSenderPreferences(track, tuning);
        if (senderUpdated) actions.push('detail-sender');
      } catch (error) {
        console.warn('[ModelFrameStreamer] Failed to apply model sender preferences.', error);
      }

      console.log('[ModelFrameStreamer] Applied LiveKit camera mode', {
        roomCode,
        modelParticipantId,
        mode: 'detail',
        actions,
        settings: mediaStreamTrack.getSettings(),
      });
      logVideoDiagnostic('info', 'model-camera-mode-applied', {
        mode: 'detail',
        actions,
        degradationPreference: 'maintain-resolution',
        maxPublishingQuality: 'high',
        target: getPreferredModelCameraTarget(mediaStreamTrack, tuning),
        modelProfileId,
        modelParticipantIds,
        participantProfileId,
        before: {
          track: beforeTrack,
          sender: beforeSender,
        },
        after: {
          track: summarizeMediaStreamTrackState(mediaStreamTrack),
          sender: summarizeSenderParameters(track.sender),
        },
      }, {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
        participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
      });
    };

    applyCameraMode();
  }, [
    isModel,
    localCameraTrack?.publication?.track,
    localParticipant?.identity,
    localParticipant?.name,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantProfileId,
    roomCode,
    tuning,
  ]);

  useEffect(() => {
    if (!isModel) return;

    const track = localCameraTrack?.publication?.track as DetailFirstVideoTrack | undefined;
    const mediaStreamTrack = track?.mediaStreamTrack;
    if (!mediaStreamTrack) return;

    const diagnosticContext = {
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
      participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    };
    const logTrackEvent = (event: Event) => {
      logVideoDiagnostic(event.type === 'ended' ? 'error' : 'warn', `model-camera-media-track-${event.type}`, {
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        participantProfileId,
        track: summarizeMediaStreamTrackState(mediaStreamTrack),
        sender: summarizeSenderParameters(track.sender),
      }, diagnosticContext);

      if (event.type === 'ended') {
        window.dispatchEvent(new CustomEvent('drawclub:model-camera-profile-republish-requested', {
          detail: { reason: 'model-camera-media-track-ended' },
        }));
      }
    };

    mediaStreamTrack.addEventListener('mute', logTrackEvent);
    mediaStreamTrack.addEventListener('unmute', logTrackEvent);
    mediaStreamTrack.addEventListener('ended', logTrackEvent);

    return () => {
      mediaStreamTrack.removeEventListener('mute', logTrackEvent);
      mediaStreamTrack.removeEventListener('unmute', logTrackEvent);
      mediaStreamTrack.removeEventListener('ended', logTrackEvent);
    };
  }, [
    isModel,
    localCameraTrack?.publication?.track,
    localParticipant?.identity,
    localParticipant?.name,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantProfileId,
    roomCode,
  ]);

  useEffect(() => {
    if (!isModel || cameraSwitching) {
      lowResolutionStreakRef.current = 0;
      lastRecoveryAtRef.current = 0;
      recoveryInProgressRef.current = false;
      lastLowResolutionRecoveryDeviceIdRef.current = null;
      return;
    }

    const track = localCameraTrack?.publication?.track as DetailFirstVideoTrack | undefined;
    if (!track?.mediaStreamTrack) {
      lowResolutionStreakRef.current = 0;
      return;
    }

    let cancelled = false;

    const getDiagnosticContext = () => ({
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
      participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    });

    const checkModelCameraQuality = async () => {
      const mediaStreamTrack = track.mediaStreamTrack;
      if (cancelled || !mediaStreamTrack || mediaStreamTrack.readyState !== 'live') return;

      const captureSample = getResolutionSampleFromSettings(mediaStreamTrack.getSettings());
      let outboundSample: VideoResolutionSample | null = null;

      try {
        outboundSample = await getLargestOutboundVideoResolution(track);
      } catch (error) {
        console.warn('[ModelFrameStreamer] Failed to read outbound camera stats.', error);
      }

      if (cancelled) return;

      const captureLow = isUnusableModelResolution(captureSample);
      const outboundLow = isUnusableModelResolution(outboundSample);
      if (!captureLow && !outboundLow) {
        lowResolutionStreakRef.current = 0;
        return;
      }

      lowResolutionStreakRef.current += 1;
      const lowResolutionStreak = lowResolutionStreakRef.current;

      logVideoDiagnostic('warn', 'model-camera-low-resolution-detected', {
        captureSample,
        outboundSample,
        captureLow,
        outboundLow,
        lowResolutionStreak,
        target: getPreferredModelCameraTarget(mediaStreamTrack, tuning),
        track: summarizeMediaStreamTrackState(mediaStreamTrack),
        sender: summarizeSenderParameters(track.sender),
      }, getDiagnosticContext());

      if (lowResolutionStreak < MODEL_CAMERA_LOW_RES_STREAK_LIMIT || recoveryInProgressRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastRecoveryAtRef.current < MODEL_CAMERA_RECOVERY_COOLDOWN_MS) {
        return;
      }

      recoveryInProgressRef.current = true;
      lastRecoveryAtRef.current = now;

      try {
        const currentDeviceId = mediaStreamTrack.getSettings().deviceId ?? null;
        const action = getModelLowResolutionRecoveryAction({
          captureLow,
          lowResolutionStreak,
          restartStreakLimit: MODEL_CAMERA_RESTART_STREAK_LIMIT,
          lastCaptureRecoveryDeviceId: lastLowResolutionRecoveryDeviceIdRef.current,
          currentDeviceId,
        });

        if (action === 'request-republish') {
          if (captureLow && currentDeviceId) {
            lastLowResolutionRecoveryDeviceIdRef.current = currentDeviceId;
          }
          window.dispatchEvent(new CustomEvent('drawclub:model-camera-profile-republish-requested', {
            detail: {
              reason: 'model-camera-low-resolution',
              captureSample,
              outboundSample,
              lowResolutionStreak,
            },
          }));
          lowResolutionStreakRef.current = 0;
        } else {
          await applyDetailFirstSenderPreferences(track, tuning);
        }
        const nextSettings = track.mediaStreamTrack?.getSettings() ?? null;
        const nextCaptureSample = nextSettings
          ? getResolutionSampleFromSettings(nextSettings)
          : null;

        if (captureLow && !isUnusableModelResolution(nextCaptureSample)) {
          lowResolutionStreakRef.current = 0;
        }

        logVideoDiagnostic('info', 'model-camera-resolution-recovery-applied', {
          action,
          forceRestart: action === 'request-republish',
          captureSample,
          outboundSample,
          nextSettings,
          nextCaptureSample,
          track: summarizeMediaStreamTrackState(track.mediaStreamTrack),
          sender: summarizeSenderParameters(track.sender),
        }, getDiagnosticContext());
        window.dispatchEvent(new CustomEvent('drawclub:camera-device-track-refreshed'));
      } finally {
        recoveryInProgressRef.current = false;
      }
    };

    checkModelCameraQuality();
    const interval = window.setInterval(checkModelCameraQuality, MODEL_CAMERA_QUALITY_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    isModel,
    cameraSwitching,
    localCameraTrack?.publication?.track,
    localParticipant?.identity,
    localParticipant?.name,
    roomCode,
    tuning,
  ]);

  // Attach the LiveKit camera only as a fallback for browsers that cannot open
  // an independent still-capture stream from the same camera.
  useEffect(() => {
    if (!isModel || !localParticipant || !fallbackVideoRef.current) {
      trackPublicationRef.current = null;
      setTrackAttached(false);
      return;
    }

    // Use the reactive track from useTracks
    const track = localCameraTrack?.publication?.track;
    if (!track) {
      trackPublicationRef.current = null;
      setTrackAttached(false);
      return;
    }

    const videoEl = fallbackVideoRef.current;
    track.attach(videoEl);
    trackPublicationRef.current = localCameraTrack.publication as LocalTrackPublication;
    setTrackAttached(true);

    return () => {
      track.detach(videoEl);
      trackPublicationRef.current = null;
      setTrackAttached(false);
    };
  }, [isModel, localParticipant, localCameraTrack]);

  useEffect(() => {
    if (!isModel || !poseActive || cameraSwitching || !videoRef.current) {
      setStillCaptureReady(false);
      setStillCaptureUnavailable(false);
      return;
    }

    if (useLiveKitStillCaptureOnly) {
      setStillCaptureReady(false);
      setStillCaptureUnavailable(true);
      setStillCaptureCameraInfo(null);
      return;
    }

    let cancelled = false;
    const videoEl = videoRef.current;
    const diagnosticContext = {
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
      participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    };

    const startStillCaptureStream = async () => {
      try {
        const liveKitTrack = localCameraTrack?.publication?.track?.mediaStreamTrack;
        const currentDeviceId = liveKitTrack?.getSettings().deviceId;
        const stillFrameRate = Math.max(0.2, tuning.stillCaptureFrameRate);
        const initialVideoConstraints: StillCaptureConstraints = {
          deviceId: currentDeviceId ? { exact: currentDeviceId } : undefined,
          width: { ideal: 9999 },
          height: { ideal: 9999 },
          frameRate: { ideal: stillFrameRate, max: stillFrameRate },
          resizeMode: 'none',
        };

        logVideoDiagnostic('info', 'model-still-capture-stream-starting', {
          liveKitTrack: liveKitTrack
            ? {
                id: liveKitTrack.id,
                label: liveKitTrack.label || null,
                enabled: liveKitTrack.enabled,
                muted: liveKitTrack.muted,
                readyState: liveKitTrack.readyState,
                settings: liveKitTrack.getSettings(),
                constraints: liveKitTrack.getConstraints(),
              }
            : null,
          requestedConstraints: {
            hasCurrentDeviceId: Boolean(currentDeviceId),
            width: 'ideal-max',
            height: 'ideal-max',
            frameRate: { ideal: stillFrameRate, max: stillFrameRate },
            resizeMode: 'none',
          },
          captureIntervalMs,
          captureKey,
        }, diagnosticContext);

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: initialVideoConstraints,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        const stillTrack = stream.getVideoTracks()[0];
        const capabilities = stillTrack?.getCapabilities?.();
        if (stillTrack && capabilities?.width?.max && capabilities?.height?.max) {
          const frameRateMax = Math.min(stillFrameRate, capabilities.frameRate?.max ?? stillFrameRate);
          const exactMaxConstraints: StillCaptureConstraints = {
            width: { exact: capabilities.width.max },
            height: { exact: capabilities.height.max },
            frameRate: { ideal: stillFrameRate, max: frameRateMax },
            resizeMode: 'none',
          };
          const idealMaxConstraints: StillCaptureConstraints = {
            width: { ideal: capabilities.width.max },
            height: { ideal: capabilities.height.max },
            frameRate: { ideal: stillFrameRate, max: frameRateMax },
            resizeMode: 'none',
          };

          try {
            await stillTrack.applyConstraints(exactMaxConstraints);
          } catch (error) {
            console.warn('[ModelFrameStreamer] Camera rejected exact max still constraints; trying ideal max constraints.', {
              error,
              maxWidth: capabilities.width.max,
              maxHeight: capabilities.height.max,
            });

            try {
              await stillTrack.applyConstraints(idealMaxConstraints);
            } catch (idealError) {
              console.warn('[ModelFrameStreamer] Camera rejected ideal max still constraints; using negotiated still stream.', idealError);
            }
          }
        }

        stillCaptureStreamRef.current = stream;
        videoEl.srcObject = stream;
        await videoEl.play();
        setStillCaptureUnavailable(false);
        setStillCaptureReady(true);

        const settings = stillTrack?.getSettings();
        if (stillTrack) {
          const cameraId = getCameraDeviceKey(settings, stillTrack.id);
          setStillCaptureCameraInfo({
            cameraId,
            cameraLabel: getStoredCameraDeviceLabel(roomCode, cameraId, 0),
          });
        }

        console.log('[ModelFrameStreamer] Still capture stream ready:', {
          settings,
          capabilities: capabilities
            ? {
                width: capabilities.width,
                height: capabilities.height,
                frameRate: capabilities.frameRate,
              }
            : null,
          captureIntervalMs,
          captureKey,
        });
        logVideoDiagnostic('info', 'model-still-capture-stream-ready', {
          settings,
          constraints: stillTrack?.getConstraints?.() ?? null,
          capabilities: capabilities
            ? {
                width: capabilities.width,
                height: capabilities.height,
                frameRate: capabilities.frameRate,
              }
            : null,
          captureIntervalMs,
          captureKey,
        }, diagnosticContext);
      } catch (error) {
        console.warn('[ModelFrameStreamer] Independent still capture unavailable; falling back to LiveKit track.', error);
        logVideoDiagnostic('warn', 'model-still-capture-stream-unavailable', {
          error,
          poseActive,
          localCameraTrack: localCameraTrack?.publication
            ? {
                trackSid: localCameraTrack.publication.trackSid,
                isMuted: localCameraTrack.publication.isMuted,
                track: localCameraTrack.publication.track?.mediaStreamTrack?.getSettings(),
              }
            : null,
        }, diagnosticContext);
        setStillCaptureCameraInfo(null);
        setStillCaptureUnavailable(true);
        setStillCaptureReady(false);
      }
    };

    startStillCaptureStream();

    return () => {
      cancelled = true;
      const stream = stillCaptureStreamRef.current;
      stillCaptureStreamRef.current = null;
      setStillCaptureReady(false);
      setStillCaptureUnavailable(false);
      setStillCaptureCameraInfo(null);
      if (stream) {
        logVideoDiagnostic('info', 'model-still-capture-stream-stopping', {
          captureKey,
          poseActive,
          videoElementHadStream: videoEl.srcObject === stream,
          tracks: stream.getTracks().map((track) => ({
            id: track.id,
            label: track.label || null,
            kind: track.kind,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            settings: track.getSettings(),
            constraints: track.getConstraints(),
          })),
        }, diagnosticContext);
      }
      if (videoEl.srcObject === stream) {
        videoEl.srcObject = null;
      }
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [cameraSwitching, captureIntervalMs, captureKey, isModel, localCameraTrack, localParticipant?.identity, localParticipant?.name, poseActive, roomCode, tuning.stillCaptureFrameRate, useLiveKitStillCaptureOnly]);

  // Use the still capture hook only for the later break-upload workflow.
  const {
    isStreaming,
    fps,
    lastFrameTime,
    startStreaming,
    stopStreaming,
  } = useModelFrameStream({
    videoRef: stillCaptureReady ? videoRef : fallbackVideoRef,
    enabled: canCaptureHighRes,
    frameIntervalMs: captureIntervalMs,
    captureKey,
    queueCaptures: shouldCaptureProductFrames,
    captureContext,
    config: highResCaptureConfig,
  });

  // Auto-start/stop local capture based on pose state.
  useEffect(() => {
    const shouldCapture = canCaptureHighRes;
    console.log('[Sender] Capture decision:', {
      shouldCapture,
      sourceMode: tuning.sourceMode,
      poseActive,
      isModel,
      stillCaptureReady,
      stillCaptureUnavailable,
      cameraSwitching,
      trackAttached,
      isStreaming,
    });
    logVideoDiagnostic('info', 'reference-pack-decision', {
      shouldCapture,
      sourceMode: tuning.sourceMode,
      poseActive,
      isModel,
      stillCaptureReady,
      stillCaptureUnavailable,
      trackAttached,
      isStreaming,
      captureIntervalMs,
      captureKey,
      highResCaptureConfig,
    }, {
      roomCode,
      participantId: localParticipant?.identity ?? null,
      participantName: localParticipant?.name ?? null,
      participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
    });

    // Start/stop the local streaming
    if (shouldCapture && !isStreaming) {
      console.log('[Sender] Starting local capture');
      startStreaming();
    } else if (!shouldCapture && isStreaming) {
      console.log('[Sender] Stopping local capture');
      stopStreaming();
      if (isModel) {
        const controller = new AbortController();
        void uploadQueuedCaptures({
          uploadPhase: 'session_end',
          limit: 10,
          signal: controller.signal,
        });
      }
    }
  }, [
    canCaptureHighRes,
    poseActive,
    isModel,
    stillCaptureReady,
    stillCaptureUnavailable,
    trackAttached,
    captureIntervalMs,
    captureKey,
    highResCaptureConfig,
    localParticipant?.identity,
    localParticipant?.name,
    roomCode,
    isStreaming,
    startStreaming,
    stopStreaming,
    uploadQueuedCaptures,
    tuning.sourceMode,
  ]);

  useEffect(() => {
    if (!isModel || !breakMode) return;

    const controller = new AbortController();
    const uploadQueue = () => uploadQueuedCaptures({
      uploadPhase: 'break',
      limit: 4,
      signal: controller.signal,
    });

    console.log('[ModelFrameStreamer] Break mode started; flushing capture queue', {
      roomCode,
      queuedCaptureCount,
    });
    uploadQueue();
    const interval = window.setInterval(uploadQueue, 5000);

    return () => {
      console.log('[ModelFrameStreamer] Break upload loop stopped', { roomCode });
      controller.abort();
      window.clearInterval(interval);
    };
  }, [breakMode, isModel, queuedCaptureCount, roomCode, uploadQueuedCaptures]);

  useEffect(() => {
    if (!isModel || poseActive || breakMode || queuedCaptureCount === 0) return;

    const controller = new AbortController();
    const uploadQueue = () => uploadQueuedCaptures({
      uploadPhase: 'session_end',
      limit: 10,
      signal: controller.signal,
    });

    console.log('[ModelFrameStreamer] Pose capture is inactive with queued stills; flushing capture queue', {
      roomCode,
      queuedCaptureCount,
      poseActive,
      breakMode,
    });
    uploadQueue();
    const interval = window.setInterval(uploadQueue, 5000);

    return () => {
      console.log('[ModelFrameStreamer] Inactive-pose upload loop stopped', { roomCode });
      controller.abort();
      window.clearInterval(interval);
    };
  }, [breakMode, isModel, poseActive, queuedCaptureCount, roomCode, uploadQueuedCaptures]);

  useEffect(() => {
    if (!isModel) return;

    getQueuedReferencePackAssetCount(roomCode)
      .then((count) => {
        console.log('[ModelFrameStreamer] Capture queue count refreshed', {
          roomCode,
          queuedCaptureCount: count,
          lastFrameTime,
        });
        setQueuedCaptureCount(count);
      })
      .catch((error) => console.error('[ModelFrameStreamer] Queue count failed:', error));
  }, [isModel, roomCode, lastFrameTime]);

  // Hidden video element for capturing frames
  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      <video
        ref={fallbackVideoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
      {/* Debug indicator - only visible in dev */}
      {import.meta.env.DEV && isStreaming && (
        <div className="absolute bottom-[4.5rem] left-3 z-10 bg-black/80 text-white text-xs px-2 py-1 rounded font-mono sm:left-4">
          HR Capture: {fps} fps · queued {queuedCaptureCount}
        </div>
      )}
    </>
  );
}
